import getCompanyPriceListApi from '@/api/get/company/company-pricing/get-company-price-list';
import getDealerSupplierPriceListApi from '@/api/get/dealer/get-dealer-supplier-price-list';
import theme from '@/common/theme';
import {
  PrimaryButton,
  PrimaryOutlineButton,
} from '@/components/button/button-component';
import Card from '@/components/card/card-component';
import Modal from '@/components/modal/modal-component';
import PermissionWrapper from '@/components/permission-wrapper/permission-wrapper-component';
import Checkbox from '@/components/table/components/table-checkbox/table-checkbox-component';
import Table from '@/components/table/table';
import Tooltip from '@/components/tooltip/tooltip-component';
import { useLazyQuery } from '@/hooks/use-api';
import { BodyText, Subtitle2Text, Subtitle3Text } from '@/utils/global-fonts';
import PropTypes from 'prop-types';
import { useCallback, useState, useEffect } from 'react';
import { FiX } from 'react-icons/fi';
import { MdOutlineEdit } from 'react-icons/md';
import CreatePricingWrapper from './create-pricing-wrapper';
import EditGlobalMarkupModal from './edit-global-markup-modal';
import EditPricingModal from './edit-pricing-modal';
import ExportPricingModal from './export-pricing-modal';
import MigratePricingWrapper from './migrate-pricing-wrapper';
import Styled from './pricing-all-style';
import AddPricingModal from './add-pricing-modal';

const headerButtonStyle = {
  padding: '10px',
  margin: '4px',
};

const multiSelectHeaderButtonStyle = {
  padding: '10px',
  margin: '4px',
  backgroundColor: 'transparent',
  color: 'White',
};

const buttonText = {
  color: 'White',
};

const CheckboxStyled = {
  bottom: 10,
};

/**
 * Page that shows all Pricing in a table format.
 * @param {object} root0 - props from the parent component.
 * @param {bool} root0.isDealer - If is a dealer
 * @param {string} root0.companyId - companyId or selected Supplier or Company
 * @returns {JSX.Element} The rendered Dealer Pricing Management page component.
 */
const PricingAllWrapper = ({ isDealer, companyId }) => {
  const tableId = isDealer ? 'dealer-pricing-list' : 'pricing-list';
  const [fetchingPresets, setFetchingPresets] = useState(true);
  const [defaultVariables, setDefaultVariables] = useState({});
  const [defaultSort, setDefaultSort] = useState('part_number:DESC');
  const [dropdown, setDropdown] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [priceId, setPriceId] = useState('');
  const [openMigrateModal, setOpenMigrateModal] = useState(false);
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openExportPriceListModal, setOpenExportPriceListModal] =
    useState(false);
  const [openGlobalMarkupModal, setOpenGlobalMarkupModal] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [shouldRefetch, setShouldRefetch] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const togglePopup = (e) => {
    if (!dropdown) {
      const pos = e.target.getBoundingClientRect();
      const x = e.target.offsetLeft;
      const y = pos.top + pos.height + 15;
      setPosition({ x, y });
    } else {
      setPosition({ x: 0, y: 0 });
    }
    setDropdown(!dropdown);
  };

  const getPriceList = useCallback(
    (query) =>
      isDealer
        ? companyId !== null && getDealerSupplierPriceListApi(companyId, query)
        : getCompanyPriceListApi(companyId, query),
    [companyId, isDealer],
  );

  const { data, count, error, refetch, loading } = useLazyQuery(getPriceList);

  const getTablePresets = useCallback(() => {
    const tablePreferences = localStorage.getItem('tablePreferences');
    if (tablePreferences) {
      const tableConfigs = JSON.parse(tablePreferences)[tableId];

      if (tableConfigs) {
        const { query } = tableConfigs;

        if (query?.length > 0) {
          // Get cached search value and apply it
          const searchQuery = query?.filter((q) => q.includes('search='));
          if (searchQuery?.length > 0) {
            const tempSearch = searchQuery[0].replace('search=', '');
            setDefaultVariables({ ...defaultVariables, search: tempSearch });
          }

          // Get cached sort value and apply it
          const sortQuery = query?.filter((q) => q.includes('sort='));
          if (sortQuery?.length > 0) {
            const tempSort = sortQuery[0].replace('sort=', '');
            setDefaultSort(tempSort);
          }

          // Get cached page size and apply it
          const pageSizeQuery = query?.filter((q) => q.includes('limit='));
          if (pageSizeQuery?.length > 0) {
            const tempPageSize = parseInt(
              pageSizeQuery[0].replace('limit=', ''),
              10,
            );
            setDefaultVariables({ ...defaultVariables, limit: tempPageSize });
          }
        }
      }
    }
    setFetchingPresets(false);
    // This is disabled because we don't want to re-run this function when the defaultVariables changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // USE EFFECTS
  useEffect(() => {
    getTablePresets();
  }, [getTablePresets]);

  const handleRowClick = useCallback(
    (clickedRow) => {
      const clickedRowId = clickedRow;
      setSelectedRows((prevSelectedRow) => {
        const isSelected = prevSelectedRow.some((row) => row === clickedRowId);

        if (isSelected) {
          // If selected, remove it
          return prevSelectedRow.filter((row) => row !== clickedRowId);
        }

        // If not selected, add it
        return [...prevSelectedRow, clickedRow];
      });
    },
    [setSelectedRows],
  );

  const renderRowCheckbox = useCallback(
    (value) => (
      <Checkbox
        width="auto"
        value={selectedRows?.some((item) => item === value.id)}
        onChange={() => {
          handleRowClick(value.id);
        }}
        style={CheckboxStyled}
      />
    ),
    [handleRowClick, selectedRows],
  );

  const renderHeaderCheckbox = useCallback(
    (value) => (
      <Checkbox
        width="auto"
        value={selectedRows?.length === value?.data?.length}
        onChange={() => {
          value?.data?.map((row) => handleRowClick(row.id));
        }}
        style={CheckboxStyled}
      />
    ),
    [handleRowClick, selectedRows?.length],
  );

  const renderPartNumber = useCallback((value) => {
    const { part_number: partNumber, temp_pn: tempPn = false } = value;

    const textContent = (
      <Subtitle3Text primary={!tempPn}>{partNumber}</Subtitle3Text>
    );

    if (!tempPn) return textContent;

    return (
      <Tooltip
        id={`temp-pn-${partNumber}`}
        text={textContent}
        toolTipText="Temporary Part Number"
      />
    );
  }, []);

  const renderEndUserPrice = useCallback((value) => {
    const { end_user_gross_profit: gp, cost_price: cp } = value;
    const endUserPrice = (cp / (1 - gp / 100)).toFixed(2);

    return <Subtitle2Text>{endUserPrice}</Subtitle2Text>;
  }, []);

  const renderTransferPrice = useCallback((value) => {
    const { transfer_price_gross_profit: gp, cost_price: cp } = value;
    const transferPrice = (cp / (1 - gp / 100)).toFixed(2);

    return <Subtitle2Text>{transferPrice}</Subtitle2Text>;
  }, []);

  const handleEdit = useCallback(
    (row) => {
      const priceDetails = row.original;
      setPriceId(priceDetails.id);
      setOpenEditModal(!openEditModal);
    },
    [openEditModal],
  );

  const Actions = useCallback(
    ({ row }) => (
      <PermissionWrapper permissions={['price.update']}>
        <Styled.StyledDiv>
          <Styled.EditIcon
            onClick={() => {
              handleEdit(row);
            }}
          />
        </Styled.StyledDiv>
      </PermissionWrapper>
    ),
    [handleEdit],
  );

  const PricingColumns = [
    {
      Header: (props) => renderHeaderCheckbox(props),
      id: 'isChecked',
      accessor: 'isChecked',
      disableSortBy: true,
      width: '10px',
      Cell: ({ row }) => renderRowCheckbox(row.original),
    },
    {
      Header: 'PRODUCT',
      id: 'name',
      accessor: 'name',
      search: true,
    },
    {
      Header: 'PART  NUMBER',
      id: 'part_number',
      accessor: 'part_number',
      search: true,
      Cell: ({ row }) => renderPartNumber(row.original),
    },
    {
      Header: 'CURRENCY',
      id: 'currency_name',
      accessor: 'currency_name',
    },
    {
      Header: 'COST PRICE',
      id: 'cost_price',
      accessor: 'cost_price',
    },
    {
      Header: 'END USER GP%',
      id: 'end_user_gross_profit',
      accessor: 'end_user_gross_profit',
    },
    {
      Header: 'END USER PRICE',
      id: 'end_user_price',
      accessor: 'end_user_price',
      Cell: ({ row }) => renderEndUserPrice(row.original),
      disableSortBy: true,
    },
    {
      Header: 'TRANSFER GP%',
      id: 'transfer_price_gross_profit',
      accessor: 'transfer_price_gross_profit',
    },
    {
      Header: 'TRANSFER PRICE',
      id: 'transfer_price',
      accessor: 'transfer_price',
      Cell: ({ row }) => renderTransferPrice(row.original),
      disableSortBy: true,
    },
    {
      Header: 'ACTIONS',
      id: 'actions',
      Cell: (props) => Actions(props),
    },
  ];

  return (
    <>
      {selectedRows?.length > 0 && (
        <Styled.StyledMainRow>
          <Styled.StyledDiv>
            <Checkbox
              value
              textColor="#fff"
              color="#ED1C24"
              textStyle
              label={`${selectedRows?.length} items Selected`}
              onChange={() => {
                setSelectedRows([]);
              }}
            />
          </Styled.StyledDiv>
          <Styled.StyledDiv>
            <PermissionWrapper permissions={['price.update']}>
              <PrimaryOutlineButton
                title="Edit"
                buttonHeight="40px"
                textStyle={buttonText}
                buttonWidth="fit-content"
                icon={<MdOutlineEdit />}
                style={multiSelectHeaderButtonStyle}
                cy="btnEditGlobalMarkup"
                onClick={() => {
                  setOpenEditModal((prevState) => !prevState);
                }}
              />
            </PermissionWrapper>
            <Styled.StepDivider />
            <PrimaryOutlineButton
              title="Cancel"
              buttonHeight="40px"
              buttonWidth="fit-content"
              textStyle={buttonText}
              icon={<FiX />}
              style={multiSelectHeaderButtonStyle}
              cy="btnEditGlobalMarkup"
              onClick={() => {
                setSelectedRows([]);
              }}
            />
          </Styled.StyledDiv>
        </Styled.StyledMainRow>
      )}
      <Table
        feature="price"
        onRowEdit={(row) => handleEdit(row)}
        columns={PricingColumns}
        data={data ?? []}
        tableOptions={['search', 'columns', 'download']}
        id={tableId}
        defaultSort={defaultSort}
        dataCount={count ?? 0}
        error={error}
        loading={loading || fetchingPresets}
        defaultVariables={defaultVariables}
        filters={[]}
        refetch={refetch}
        key={companyId}
        tableHeader={selectedRows?.length < 1}
        tableHeaderProps={
          <PermissionWrapper permissions={['price.create']}>
            <PrimaryButton
              title="Actions"
              buttonHeight="40px"
              buttonWidth="110px"
              style={headerButtonStyle}
              textStyle={{ marginLeft: 15 }}
              cy="btnCreatePriceList"
              onClick={togglePopup}
              iconPosition="right"
              icon={
                <Styled.DropDownIcon
                  className="dropdown-icon"
                  color={theme.COLOR_WHITE}
                />
              }
            />
            {dropdown && (
              <>
                <Styled.OutsideClickHandler onClick={() => togglePopup()} />
                <Styled.DropDown $position={position}>
                  <Card
                    className="card"
                    width="200px"
                    margin="0px 0px"
                    borderRadius="4px 4px 4px 4px"
                    padding="6px 0px 6px 0px"
                    bodyStyle={{
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                      borderTop: 'none',
                    }}
                  >
                    <Styled.OptionNavlink
                      permissions={['price.create']}
                      onClick={() => {
                        setDropdown((prevDropdown) => !prevDropdown);
                        setOpenCreateModal((prevState) => !prevState);
                      }}
                    >
                      <Styled.AddPriceListIcon size={16} />
                      <BodyText color={theme.COLOR_TEXT}>
                        Add Price list
                      </BodyText>
                    </Styled.OptionNavlink>
                    <Styled.OptionNavlink
                      permissions={['price.create']}
                      onClick={() => {
                        setDropdown((prevDropdown) => !prevDropdown);
                        setOpenAddModal((prevState) => !prevState);
                      }}
                    >
                      <Styled.AddPriceRowIcon size={16} />
                      <BodyText color={theme.COLOR_TEXT}>Add a Row</BodyText>
                    </Styled.OptionNavlink>
                    {!isDealer && (
                      <Styled.OptionNavlink
                        permissions={['price.update']}
                        onClick={() => {
                          setDropdown((prevDropdown) => !prevDropdown);
                          setOpenMigrateModal((prevState) => !prevState);
                        }}
                      >
                        <Styled.MigratePriceListIcon size={16} />
                        <BodyText color={theme.COLOR_TEXT}>
                          Migrate Price List
                        </BodyText>
                      </Styled.OptionNavlink>
                    )}
                    <Styled.OptionNavlink
                      permissions={['price.update']}
                      onClick={() => {
                        setDropdown((prevDropdown) => !prevDropdown);
                        setOpenGlobalMarkupModal((prevState) => !prevState);
                      }}
                    >
                      <Styled.EditGlobalMarkupIcon size={16} />
                      <BodyText color={theme.COLOR_TEXT}>
                        Edit Global Markup
                      </BodyText>
                    </Styled.OptionNavlink>
                  </Card>
                </Styled.DropDown>
              </>
            )}
          </PermissionWrapper>
        }
        modalExport
        getDownloadData={() => {
          setOpenExportPriceListModal((prevState) => !prevState);
        }}
        shouldRefetch={shouldRefetch}
        setShouldRefetch={setShouldRefetch}
      />
      <Modal
        width="500px"
        open={openGlobalMarkupModal}
        height="auto"
        showClose
        bodyStyle={{
          padding: '0px',
        }}
        close={() => {
          setOpenGlobalMarkupModal(false);
        }}
        title="Edit Global Markup"
      >
        <EditGlobalMarkupModal
          tableCount={count}
          isDealer={isDealer}
          refetch={refetch}
          companyId={companyId}
          onClose={setOpenGlobalMarkupModal}
        />
      </Modal>
      <Modal
        width="500px"
        open={openMigrateModal}
        height="fit-content"
        showClose
        bodyStyle={{
          padding: '0px',
        }}
        close={() => {
          setOpenMigrateModal(false);
        }}
        title="Migrate Price List"
      >
        <MigratePricingWrapper
          companyId={companyId}
          isDealer={isDealer}
          refetch={refetch}
          onClose={setOpenMigrateModal}
        />
      </Modal>
      <Modal
        width="500px"
        open={openEditModal}
        height="fit-content"
        showClose
        bodyStyle={{
          padding: '0px',
        }}
        close={() => {
          setOpenEditModal(false);
        }}
        title="Edit Pricing"
      >
        <EditPricingModal
          priceId={priceId}
          isDealer={isDealer}
          refetch={refetch}
          setSelectedRows={setSelectedRows}
          companyId={companyId}
          selectedRows={selectedRows}
          onClose={setOpenEditModal}
        />
      </Modal>
      <Modal
        width="500px"
        open={openAddModal}
        height="fit-content"
        showClose
        bodyStyle={{
          padding: '0px',
        }}
        close={() => {
          setOpenAddModal(false);
        }}
        title="Add Pricing Row"
      >
        <AddPricingModal
          isDealer={isDealer}
          refetch={refetch}
          setSelectedRows={setSelectedRows}
          companyId={companyId}
          selectedRows={selectedRows}
          onClose={setOpenAddModal}
        />
      </Modal>
      <Modal
        width="500px"
        open={openExportPriceListModal}
        height="auto"
        showClose
        bodyStyle={{
          padding: '0px',
        }}
        close={() => {
          setOpenExportPriceListModal(false);
        }}
        title="Export Pricelist"
      >
        <ExportPricingModal
          onClose={() => setOpenExportPriceListModal(false)}
          companyId={companyId}
          isDealer={isDealer}
        />
      </Modal>
      <Modal
        width="500px"
        open={openCreateModal}
        height="auto"
        showClose
        bodyStyle={{
          padding: '0px',
        }}
        close={() => {
          setOpenCreateModal(false);
        }}
        title="Add Price List"
      >
        <CreatePricingWrapper
          isDealer={isDealer}
          companyId={companyId}
          setOpenCreateModal={setOpenCreateModal}
          refetch={refetch}
        />
      </Modal>
    </>
  );
};

PricingAllWrapper.propTypes = {
  isDealer: PropTypes.bool.isRequired,
  companyId: PropTypes.string.isRequired,
};

PricingAllWrapper.defaultProps = {};

export default PricingAllWrapper;
